import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  ScrollView,
  FlatList,
  StyleSheet,
  RefreshControl,
} from 'react-native';

import { useResponsive } from '@/hooks/common/useResponsive';
import { useResourcesScreen } from '@/hooks/residents/useResourcesScreen';
import { DesktopResourcesViewSkeleton } from './DesktopResourcesViewSkeleton';
import { CommunityResource } from '@/types/residents/communityResource';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { EmptyState } from '@/components/common/residents/EmptyState';
import { PageHeader, SectionHeader } from '@/components/residents/redesign';
import {
  ResourceGlassTile,
  ResourceGlassPanel,
} from '@/components/residents/redesign/resources';
import { strings } from '@/constants/residents';

const HP = REDESIGN_SPACING.horizontalPadding;
const GRID_GAP = 16;

export const DesktopCommunityResourcesView: React.FC = () => {
  const { colors } = useThemeColors();
  const { layoutMode, width } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const numColumns = width >= 768 ? 3 : 2;

  const {
    resources,
    isLoading,
    refreshing,
    subtitle,
    resourceCount,
    handleRefresh,
  } = useResourcesScreen();

  const [selected, setSelected] = useState<CommunityResource | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const data = useMemo(() => resources || [], [resources]);

  const handleSelect = useCallback((resource: CommunityResource) => {
    setSelected(resource);
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  const renderTile = useCallback(({ item }: { item: CommunityResource }) => (
    <ResourceGlassTile item={item} onPress={handleSelect} />
  ), [handleSelect]);

  const keyExtractor = useCallback((item: CommunityResource) => String(item.id), []);

  if (!isDesktop) {
    return null;
  }

  const sectionSubtitle = resourceCount > 0
    ? `${resourceCount} resource${resourceCount !== 1 ? 's' : ''}`
    : undefined;

  return (
    <View style={styles.container}>
      <ResourceGlassPanel resource={selected} open={panelOpen} onClose={handleClosePanel} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.text.primary}
          />
        }
      >
        <PageHeader title="Resources" subtitle={subtitle} />

        <SectionHeader
          title="Community Resources"
          actionLabel={sectionSubtitle}
        />

        {isLoading && data.length === 0 && (
          <DesktopResourcesViewSkeleton numColumns={numColumns} />
        )}

        {!isLoading && data.length === 0 && (
          <View style={styles.emptyWrap}>
            <EmptyState
              title={strings.communityResources.empty.title}
              description={strings.communityResources.empty.description}
              iconName="document-outline"
            />
          </View>
        )}

        {data.length > 0 && (
          <FlatList
            data={data}
            keyExtractor={keyExtractor}
            numColumns={numColumns}
            key={numColumns}
            columnWrapperStyle={styles.columnWrapper}
            contentContainerStyle={styles.gridContent}
            renderItem={renderTile}
            scrollEnabled={false}
          />
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: HP,
    paddingBottom: 60,
  },
  gridContent: {
    gap: GRID_GAP,
  },
  columnWrapper: {
    gap: GRID_GAP,
  },
  emptyWrap: {
    paddingVertical: 40,
  },
});

export default DesktopCommunityResourcesView;
